import { ThemeEvents } from '@theme/events';

class ProductPoints extends HTMLElement {
  #abortController;

  connectedCallback() {
    const section = this.closest('.shopify-section, dialog');
    if (!section) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    section.addEventListener(ThemeEvents.variantUpdate, this.updatePoints, {
      signal: this.#abortController.signal,
    });
    section.addEventListener('change', this.updatePurchaseOption, {
      signal: this.#abortController.signal,
    });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.updateQuantity, {
      signal: this.#abortController.signal,
    });
    this.#refreshPoints();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  updatePoints = (event) => {
    const { data } = event.detail;
    if (!data.newProduct && data.productId !== this.dataset.productId) return;

    const newPoints = Array.from(data.html.querySelectorAll('product-points')).find(
      (points) => points.dataset.blockId === this.dataset.blockId
    );
    if (!newPoints) return;

    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    }

    this.dataset.variantPrice = newPoints.dataset.variantPrice;
    this.dataset.sellingPlanAllocations = newPoints.dataset.sellingPlanAllocations;
    this.dataset.pointsMultiplier = newPoints.dataset.pointsMultiplier;

    if (this.innerHTML !== newPoints.innerHTML) {
      this.innerHTML = newPoints.innerHTML;
    }
    this.#refreshPoints();
  };

  updatePurchaseOption = (event) => {
    if (!(event.target instanceof Element)) return;

    const picker = event.target.closest('skio-plan-picker');
    if (picker?.dataset.productId !== this.dataset.productId) return;

    this.#refreshPoints();
  };

  updateQuantity = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.name !== 'quantity') return;

    const form = event.target.closest('product-form-component, sticky-product-form-component');
    if (form?.dataset.productId !== this.dataset.productId) return;

    if (
      form.matches('product-form-component') &&
      form.closest('.shopify-section, dialog') !== this.closest('.shopify-section, dialog')
    ) return;

    this.#renderPoints(event.detail.quantity);
  };

  #refreshPoints() {
    queueMicrotask(() => {
      if (!this.isConnected) return;

      const section = this.closest('.shopify-section, dialog');
      const form = Array.from(section?.querySelectorAll('product-form-component') ?? []).find(
        (form) => form.dataset.productId === this.dataset.productId
      );
      const input = form?.querySelector('input[name="quantity"]');
      this.#renderPoints(Number(input?.value ?? 1));
    });
  }

  #renderPoints(quantity) {
    if (!Number.isFinite(quantity) || quantity < 1) return;

    let price = Number(this.dataset.variantPrice);
    const section = this.closest('.shopify-section, dialog');
    const picker = Array.from(section?.querySelectorAll('skio-plan-picker') ?? []).find(
      (picker) => picker.dataset.productId === this.dataset.productId
    );
    const sellingPlanInput = picker?.querySelector('select[name="selling_plan"]');

    if (sellingPlanInput?.value) {
      const allocations = JSON.parse(this.dataset.sellingPlanAllocations || '[]');
      const allocation = allocations.find(
        (allocation) => String(allocation.selling_plan_id) === sellingPlanInput.value
      );
      const subscriptionPrice = allocation?.price;
      const hasSubscriptionPrice =
        typeof subscriptionPrice === 'number' && Number.isFinite(subscriptionPrice) && subscriptionPrice >= 0;
      price = hasSubscriptionPrice && subscriptionPrice !== price ? subscriptionPrice : price * 0.9;
    }

    const points = Math.round(
      (price * Number(this.dataset.pointsMultiplier) * quantity) / 100
    );
    if (!Number.isFinite(points)) return;

    this.querySelectorAll('[data-points-value]').forEach((value) => {
      if (value.textContent !== String(points)) value.textContent = String(points);
    });
  }
}

if (!customElements.get('product-points')) {
  customElements.define('product-points', ProductPoints);
}
